import * as turf from '@turf/turf';
import { brightenColor, lightenColor, darkenColor } from './Colors';


class DrawingManager {
    constructor(mapRef, mapViews, setMapViews, clearHighlight, onHexagonSelect = null) {
        this.mapRef = mapRef;
        this.mapViews = mapViews;
        this.setMapViews = setMapViews;
        this.clearHighlight = clearHighlight;
        this.currentTool = null;
        this.isDrawing = false;
        this.points = [];
        this.tempLayerId = null;
        this.selectedHexagons = new Set();
        this.selectionMode = 'single';
        this.onHexagonSelect = onHexagonSelect;
        // цвет, который используется, если originalColor отсутствует (null-ячейки)
        this.baseNullColor = 'rgba(50,50,50,0.6)';
    }

    getMap() {
        const map = this.mapRef.current;
        if (map && typeof map === 'object') {
            if (map.getMaplibreMap) return map.getMaplibreMap();
            if (map.getMap) return map.getMap();
            if (map._map) return map._map;
            return map;
        }
        return null;
    }

    setSelectionMode(mode) { this.selectionMode = mode; }

    setTool(tool) {
        this.currentTool = tool;
        this.clearDrawing();
        const map = this.getMap();
        if (map && map.getCanvas) {
            const canvas = map.getCanvas();
            canvas.style.cursor = tool ? this.getCursorForTool(tool) : '';
        }
    }

    startDrawing(lngLat) {
        if (!this.currentTool || this.currentTool === 'select') return;

        if (this.selectionMode === 'single' && this.selectedHexagons.size > 0) {
            this.selectedHexagons.clear();
            this.updateHexagonHighlight();
            if (this.onHexagonSelect) this.onHexagonSelect(null);
        }

        this.isDrawing = true;
        this.points = [[lngLat.lng, lngLat.lat]];
        this.createTempLayer();
    }

    getCursorForTool(tool) {
        const cursors = {
            select: 'pointer',
            rectangle: 'crosshair',
            circle: 'crosshair',
            lasso: 'crosshair'
        };
        return cursors[tool] || 'default';
    }

    clearDrawing() {
        this.isDrawing = false;
        this.points = [];
        this.removeTempLayer();
    }

    clearSelection() {
        this.selectedHexagons.clear();
        this.updateHexagonHighlight();
        this.clearDrawing();
    }

    /** Основной метод, который вызывается при любом изменении выделения */
    updateHexagonHighlight() {
        const map = this.getMap();
        const SOURCE = 'area-source';
        if (!map) return;

        if (this.selectedHexagons.size === 0) {
            try { map.removeFeatureState({ source: SOURCE }); } catch (e) {}
            this.updateHighlightOutline();
            return;
        }

        this.mapViews.forEach(feature => {
            const objectId = feature.properties.object_id;
            const isSelected = this.selectedHexagons.has(objectId);
            const originalColor = feature.properties.originalColor || feature.properties.color;

            try {
                if (isSelected) {
                    map.setFeatureState(
                        { source: SOURCE, id: objectId },
                        {
                            color: originalColor || this.baseNullColor,
                            outlineColor: 'rgba(0,0,0,0)'   // ← убираем внутренние границы
                        }
                    );
                } else {
                    map.setFeatureState(
                        { source: SOURCE, id: objectId },
                        {
                            color: darkenColor(originalColor || this.baseNullColor, 0.6),
                            outlineColor: 'rgba(0,0,0,0.2)'
                        }
                    );
                }
            } catch (e) {}
        });

        this.updateHighlightOutline(); // строит внешний контур
    }

    /** Вычисляет и отправляет в источник 'highlight-outline-source' контур(ы) объединённой области выделения */
    updateHighlightOutline() {
        const map = this.getMap();
        if (!map) return;

        const source = map.getSource('highlight-outline-source');
        if (!source) {
            console.warn('Source "highlight-outline-source" not found');
            return;
        }

        if (this.selectedHexagons.size === 0) {
            source.setData({ type: 'FeatureCollection', features: [] });
            return;
        }

        const edgeCount = new Map();
        const edgeCoords = new Map();

        this.mapViews.forEach(feature => {
            if (!this.selectedHexagons.has(feature.properties.object_id)) return;
            if (!feature.geometry || feature.geometry.type !== 'Polygon') return;

            const coords = feature.geometry.coordinates[0];
            for (let i = 0; i < coords.length - 1; i++) {
                const p1 = coords[i];
                const p2 = coords[i + 1];
                const key = this._makeEdgeKey(p1, p2);
                edgeCount.set(key, (edgeCount.get(key) || 0) + 1);
                if (!edgeCoords.has(key)) {
                    edgeCoords.set(key, [p1, p2]);
                }
            }
        });

        const features = [];
        edgeCount.forEach((count, key) => {
            if (count === 1) {
                const [p1, p2] = edgeCoords.get(key);
                features.push({
                    type: 'Feature',
                    geometry: { type: 'LineString', coordinates: [p1, p2] },
                    properties: {}
                });
            }
        });

        source.setData({
            type: 'FeatureCollection',
            features
        });
    }

    /** Вспомогательный метод для создания уникального ключа ребра (независимо от направления) */
    _makeEdgeKey(p1, p2) {
        const round = (v) => Math.round(v * 1e6) / 1e6; // округление до 6 знаков
        const a = [round(p1[0]), round(p1[1])];
        const b = [round(p2[0]), round(p2[1])];
        const key = (a[0] < b[0] || (a[0] === b[0] && a[1] < b[1]))
            ? `${a[0]},${a[1]}|${b[0]},${b[1]}`
            : `${b[0]},${b[1]}|${a[0]},${a[1]}`;
        return key;
    }

    getHexagonAtPoint(lngLat) {
        const map = this.getMap();
        if (!map) return null;

        const point = map.project([lngLat.lng, lngLat.lat]);
        const features = map.queryRenderedFeatures(point, {
            layers: ['area-layer']
        });

        if (features.length > 0) {
            const feature = features[0];
            const objectId = feature.properties.object_id;
            const strictMatch = this.mapViews.find(f => f.properties.object_id === objectId);
            const match = strictMatch
                ?? this.mapViews.find(f => String(f.properties.object_id) === String(objectId));
            // #region agent log
            fetch('http://127.0.0.1:7591/ingest/d2880673-f438-4573-9075-68f79f8bcef7',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'da59d4'},body:JSON.stringify({sessionId:'da59d4',runId:'hex-legend',hypothesisId:'H1',location:'DrawingManager.js:getHexagonAtPoint',message:'hex click id compare',data:{queryType:typeof objectId,viewType:typeof this.mapViews[0]?.properties?.object_id,strictHit:!!strictMatch,looseHit:!!match,objectId},timestamp:Date.now()})}).catch(()=>{});
            // #endregion
            return match || null;
        }
        return null;
    }

    continueDrawing(lngLat) {
        if (!this.isDrawing) return;
        this.points.push([lngLat.lng, lngLat.lat]);
        this.updateTempLayer();
    }

    finishDrawing() {
        if (!this.isDrawing) return;
        this.isDrawing = false;

        if (this.points.length < 2) {
            this.clearDrawing();
            return;
        }

        let polygon;
        switch (this.currentTool) {
            case 'rectangle': polygon = this.createRectangle(); break;
            case 'circle':    polygon = this.createCircle();    break;
            case 'lasso':     polygon = this.createPolygon();   break;
            default: this.clearDrawing(); return;
        }

        if (polygon) {
            this.selectHexagonsInPolygon(polygon);
        }
        this.clearDrawing();
    }

    createRectangle() {
        if (this.points.length < 2) return null;
        const [p1, p2] = [this.points[0], this.points[this.points.length - 1]];
        const minX = Math.min(p1[0], p2[0]);
        const maxX = Math.max(p1[0], p2[0]);
        const minY = Math.min(p1[1], p2[1]);
        const maxY = Math.max(p1[1], p2[1]);
        return turf.polygon([[[minX, minY],[maxX, minY],[maxX, maxY],[minX, maxY],[minX, minY]]]);
    }

    createCircle() {
        if (this.points.length < 2) return null;
        const center = this.points[0];
        const lastPoint = this.points[this.points.length - 1];
        const from = turf.point(center);
        const to = turf.point(lastPoint);
        const distance = turf.distance(from, to, { units: 'meters' });
        return turf.circle(center, distance, { steps: 64, units: 'meters' });
    }

    createPolygon() {
        if (this.points.length < 3) return null;
        const closedPoints = [...this.points, this.points[0]];
        return turf.polygon([closedPoints]);
    }

    clearSelectionOnly() {
        console.log('[DrawingManager] Clearing selection');
        this.selectedHexagons.clear();
        this.updateHexagonHighlight();
        this.clearDrawing();

        const map = this.getMap();
        if (map) {
            try {
                const source = map.getSource('labels-source');
                if (source && typeof source.setData === 'function') {
                    source.setData({ type: 'FeatureCollection', features: [] });
                }
            } catch (e) {
                console.warn('[DrawingManager] Error clearing labels source:', e);
            }
        }

        if (this.onHexagonSelect) {
            this.onHexagonSelect(null);
        }
    }

    selectHexagonsInPolygon(polygon) {
        const selectedIds = [];

        this.mapViews.forEach(feature => {
            if (!feature.geometry) return;
            try {
                const hexagonPolygon = turf.polygon(feature.geometry.coordinates);
                const isIntersecting = turf.booleanIntersects(polygon, hexagonPolygon);
                const isWithin = turf.booleanContains(polygon, hexagonPolygon);
                const isWithinHex = turf.booleanContains(hexagonPolygon, polygon);

                if (isIntersecting || isWithin || isWithinHex) {
                    const objectId = feature.properties.object_id;
                    if (!this.selectedHexagons.has(objectId)) {
                        this.selectedHexagons.add(objectId);
                        selectedIds.push(objectId);
                    }
                }
            } catch (error) {
                console.warn('Error checking intersection:', error);
            }
        });

        if (selectedIds.length > 0) {
            this.updateHexagonHighlight();
            console.log(`Selected ${selectedIds.length} hexagons:`, selectedIds);

            if (this.onHexagonSelect) {
                const selectedHexagons = this.mapViews.filter(f =>
                    this.selectedHexagons.has(f.properties.object_id)
                );
                this.onHexagonSelect(selectedHexagons);
            }
        }
        return selectedIds;
    }

    createTempLayer() {
        this.removeTempLayer();

        const tempData = {
            type: 'FeatureCollection',
            features: []
        };

        const map = this.getMap();
        if (!map || typeof map.addSource !== 'function') return;

        try {
            map.addSource('temp-drawing-source', {
                type: 'geojson',
                data: tempData
            });

            map.addLayer({
                id: 'temp-drawing-line',
                type: 'line',
                source: 'temp-drawing-source',
                paint: {
                    'line-color': '#21c36e',
                    'line-width': 3,
                    'line-dasharray': [4, 4]
                }
            });

            map.addLayer({
                id: 'temp-drawing-fill',
                type: 'fill',
                source: 'temp-drawing-source',
                paint: {
                    'fill-color': '#21c36e',
                    'fill-opacity': 0.2
                }
            });

            try {
                map.moveLayer('labels-layer');
            } catch(e) {}

            this.tempLayerId = 'temp-drawing-source';
        } catch (error) {
            console.warn('Error creating temp layer:', error);
        }
    }

    updateTempLayer() {
        const map = this.getMap();
        if (!map || !this.tempLayerId) return;

        let feature;

        switch (this.currentTool) {
            case 'rectangle':
                if (this.points.length >= 2) {
                    feature = this.createRectanglePreview();
                }
                break;
            case 'circle':
                if (this.points.length >= 2) {
                    feature = this.createCirclePreview();
                }
                break;
            case 'lasso':
                if (this.points.length >= 2) {
                    feature = this.createPolygonPreview();
                }
                break;
        }

        if (feature) {
            try {
                const source = map.getSource(this.tempLayerId);
                if (source) {
                    source.setData({
                        type: 'FeatureCollection',
                        features: [feature]
                    });
                }
            } catch (error) {
                console.warn('Error updating temp layer:', error);
            }
        }
    }

    createRectanglePreview() {
        const [p1, p2] = [this.points[0], this.points[this.points.length - 1]];
        const minX = Math.min(p1[0], p2[0]);
        const maxX = Math.max(p1[0], p2[0]);
        const minY = Math.min(p1[1], p2[1]);
        const maxY = Math.max(p1[1], p2[1]);

        return {
            type: 'Feature',
            geometry: {
                type: 'Polygon',
                coordinates: [[
                    [minX, minY],
                    [maxX, minY],
                    [maxX, maxY],
                    [minX, maxY],
                    [minX, minY]
                ]]
            },
            properties: {}
        };
    }

    createCirclePreview() {
        const center = this.points[0];
        const lastPoint = this.points[this.points.length - 1];

        const from = turf.point(center);
        const to = turf.point(lastPoint);
        const distance = turf.distance(from, to, { units: 'meters' });

        return turf.circle(center, distance, { steps: 64, units: 'meters' });
    }

    createPolygonPreview() {
        return {
            type: 'Feature',
            geometry: {
                type: 'LineString',
                coordinates: this.points
            },
            properties: {}
        };
    }

    removeTempLayer() {
        const map = this.getMap();
        if (!map) return;

        try {
            if (map.getLayer('temp-drawing-line')) {
                map.removeLayer('temp-drawing-line');
            }
            if (map.getLayer('temp-drawing-fill')) {
                map.removeLayer('temp-drawing-fill');
            }
            if (map.getSource('temp-drawing-source')) {
                map.removeSource('temp-drawing-source');
            }
        } catch (error) {
            // Игнорируем ошибки при удалении
        }

        this.tempLayerId = null;
    }

    handleMouseMove(e) {
        if (!this.isDrawing) return;
        this.continueDrawing(e.lngLat);
    }

    handleClick(e) {
        if (this.currentTool && this.currentTool !== 'select') {
            if (!this.isDrawing) {
                this.startDrawing(e.lngLat);
            } else {
                this.finishDrawing();
            }
            return;
        }

        // Инструмент выбора (select)
        if (this.currentTool === 'select') {
            const hexagon = this.getHexagonAtPoint(e.lngLat);
            if (!hexagon) return;

            const objectId = hexagon.properties.object_id;

            if (this.selectionMode === 'single') {
                if (this.selectedHexagons.size === 1 && this.selectedHexagons.has(objectId)) {
                    this.selectedHexagons.clear();
                    this.updateHexagonHighlight();
                    if (this.onHexagonSelect) this.onHexagonSelect(null);
                } else {
                    this.selectedHexagons.clear();
                    this.selectedHexagons.add(objectId);
                    this.updateHexagonHighlight();
                    if (this.onHexagonSelect) this.onHexagonSelect([hexagon]); // ← ОБЕРНУТЬ В МАССИВ
                }
            }
            else if (this.selectionMode === 'multi') {
                // Множественный режим
                if (this.selectedHexagons.has(objectId)) {
                    this.selectedHexagons.delete(objectId);
                } else {
                    this.selectedHexagons.add(objectId);
                }
                this.updateHexagonHighlight();

                if (this.onHexagonSelect) {
                    const selectedHexagons = this.mapViews.filter(f =>
                        this.selectedHexagons.has(f.properties.object_id)
                    );
                    this.onHexagonSelect(selectedHexagons);
                }
            }
        }
    }

    handleDoubleClick(e) {
        if (this.isDrawing && this.currentTool === 'lasso') {
            this.finishDrawing();
            return;
        }

        if (this.currentTool === 'select' && this.selectionMode === 'multi') {
            const hexagon = this.getHexagonAtPoint(e.lngLat);
            if (!hexagon) return;

            const objectId = hexagon.properties.object_id;

            if (this.selectedHexagons.has(objectId)) {
                this.selectedHexagons.delete(objectId);
                this.updateHexagonHighlight();

                if (this.onHexagonSelect) {
                    const selectedHexagons = this.mapViews.filter(f =>
                        this.selectedHexagons.has(f.properties.object_id)
                    );
                    this.onHexagonSelect(selectedHexagons);
                }
            }
        }
    }

    handleContextMenu(e) {
        if (this.isDrawing) {
            e.preventDefault();
            this.clearDrawing();
            return false;
        }
    }

    reset() {
        this.clearSelection();
        this.clearDrawing();
        this.currentTool = null;

        const map = this.getMap();
        if (map && map.getCanvas) {
            const canvas = map.getCanvas();
            canvas.style.cursor = '';
        }
    }

    getSelectedIds() {
        return Array.from(this.selectedHexagons);
    }

    setSelectedIds(ids) {
        this.selectedHexagons.clear();
        ids.forEach(id => this.selectedHexagons.add(id));
        this.updateHexagonHighlight();
    }

    onSelectionChange(callback) {
        this.onSelectionChangeCallback = callback;
    }
}


export default DrawingManager;